import * as React from "react"
import { Link } from "gatsby"
import ReCAPTCHA from "react-google-recaptcha"

import {
  ContactFormContext,
  SubjectType,
} from "../../core/contexts/contactForm"
import {
  ContactFormActionType,
  ContactFormResultType,
} from "../../common/enums"
import ElevatedButton from "../buttons/elevatedButton"
import Modal from "../modal/modal"

import "./contactForm.scss"

type ContactFormProps = {
  defaultSubject?: SubjectType
  subjects?: {
    value: SubjectType
    label: string
  }[]
}

const ContactForm = ({
  defaultSubject = "general_enquiry",
  subjects = [
    { value: "general_enquiry", label: "General Enquiry" },
    { value: "business_solution", label: "Business Solution" },
    { value: "strategy", label: "Strategy" },
    { value: "product_development", label: "Product Development" },
    { value: "digital_content", label: "Digital Content" },
  ],
}: ContactFormProps) => {
  const [state, dispatch] = React.useContext(ContactFormContext)
  const recaptchaRef = React.useRef<any>(null)

  React.useEffect(() => {
    dispatch({
      type: ContactFormResultType.SetDefaultSubject,
      payload: { defaultSubject },
    })
  }, [])

  React.useEffect(() => {
    if (state.captchaToken === null) recaptchaRef.current.reset()
  }, [state.captchaToken])

  const toggleIsModalOpen = () =>
    dispatch({
      type: ContactFormResultType.SetModalOpen,
      payload: { isModalOpen: !state.isModalOpen },
    })

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    dispatch({
      type: ContactFormResultType.SetDataName,
      payload: { name: e.target.value },
    })

  const onEmailAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: ContactFormResultType.SetDataFrom,
      payload: { from: e.target.value },
    })
    e.target.setCustomValidity("")
    if (e.target.validity.valid) {
      e.target.setCustomValidity("")
    } else {
      e.target.setCustomValidity("Please enter a valid email address.")
    }
  }

  const onCompanyChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    dispatch({
      type: ContactFormResultType.SetDataCompany,
      payload: { company: e.target.value },
    })

  const onSubjectChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    dispatch({
      type: ContactFormResultType.SetDataSubject,
      payload: { subject: e.target.value },
    })

  const onMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) =>
    dispatch({
      type: ContactFormResultType.SetDataMessage,
      payload: { message: e.target.value },
    })

  const onCaptchaChange = (token: string | null) => {
    dispatch({
      type: ContactFormResultType.SetCaptchaToken,
      payload: { captchaToken: token },
    })
    if (token !== null) verifyReCAPTCHA(token)
  }

  const verifyReCAPTCHA = (token: string) => {
    dispatch({
      type: ContactFormActionType.VerifyReCaptcha,
      payload: { captchaToken: token },
    })
  }

  const submitForm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (state.isLoading) return

    if (!state.isVerified) {
      dispatch({
        type: ContactFormResultType.SetModalState,
        payload: { modalState: "no_captcha" },
      })
      toggleIsModalOpen()
      return
    }

    dispatch({
      type: ContactFormActionType.SendEmail,
      payload: {
        ...state.data,
        subject: `[ContactForm] ${state.data.subject}`,
      },
    })
  }

  return (
    <div>
      <form autoComplete="on" onSubmit={submitForm}>
        <div className="row justify-space-between mb-2">
          <div className="group col-xs-12 col-md-5 mb-5">
            <input
              type="text"
              id="contact-form-full-name"
              required
              data-value={state.data.name}
              value={state.data.name}
              onChange={onNameChange}
            />
            <span className="highlight" />
            <span className="bar" />
            <label htmlFor="contact-form-full-name">Full Name *</label>
          </div>
          <div className="group col-xs-12 col-md-6 mb-5">
            <input
              type="email"
              id="contact-form-email-address"
              required
              data-value={state.data.from}
              value={state.data.from}
              onChange={onEmailAddressChange}
            />
            <span className="highlight" />
            <span className="bar" />
            <label htmlFor="contact-form-email-address">Email Address *</label>
          </div>
          <div className="group col-xs-12 col-md-6 mb-5">
            <input
              type="text"
              id="contact-form-company"
              data-value={state.data.company ?? ""}
              value={state.data.company ?? ""}
              onChange={onCompanyChange}
            />
            <span className="highlight" />
            <span className="bar" />
            <label htmlFor="contact-form-company">Company</label>
          </div>
          <div className="group col-xs-12 mb-5">
            <select
              id="contact-form-subject"
              required
              data-value={state.data.subject}
              value={state.data.subject}
              onChange={onSubjectChange}
            >
              {subjects.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <span className="highlight" />
            <span className="bar" />
            <label htmlFor="contact-form-subject">Subject *</label>
          </div>
          <div className="group col-xs-12 mb-5">
            <textarea
              rows={4}
              id="contact-form-message"
              required
              data-value={state.data.message}
              value={state.data.message}
              onChange={onMessageChange}
            />
            <span className="highlight" />
            <span className="bar" />
            <label htmlFor="contact-form-message">Your Message *</label>
          </div>
          <div className="mb-5">
            <p className="security-input">Security Validation *</p>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.GATSBY_RECAPTCHA_SITE_KEY ?? ""}
              onChange={onCaptchaChange}
            />
          </div>
          <div className="mb-5">
            <p className="overline">
              iPhupha Digital is committed to protecting your information. Your
              information will be used in accordance with the applicable data
              privacy law, our internal policies and our{" "}
              <Link className="text-tertiary text-hover-black" to="/privacy">
                privacy policy
              </Link>
              . As iPhupha Digital is a global company, your information may be
              stored and processed by iPhupha Digital and its affiliates in
              countries outside your country of residence, but wherever your
              information is processed, we will handle it with the same care and
              respect for your privacy.
            </p>
          </div>
          <div className="col-xs-12 mb-5">
            <ElevatedButton label="Send Message" />
          </div>
        </div>
      </form>

      <Modal
        open={state.isModalOpen}
        title="Send Message"
        handleClose={toggleIsModalOpen}
      >
        <p>{state.modalMessage}</p>
      </Modal>
    </div>
  )
}

export default ContactForm
